import React, { useState, useEffect } from 'react';
import { 
  PlusCircle, 
  FileText, 
  Clock, 
  Trash2,
  Eye,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  User,
  LogOut,
  Calendar,
  BarChart3,
  Shield
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { analysisAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import CreateAnalysis from './CreateAnalysis';
import AnalysisResults from './AnalysisResults';

const Dashboard = () => {
  const { user, logout } = useAuth();
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState('dashboard'); // 'dashboard', 'create', 'results'
  const [selectedAnalysisId, setSelectedAnalysisId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState('');

  useEffect(() => {
    fetchAnalyses();
  }, []);

  const fetchAnalyses = async () => {
    try {
      const data = await analysisAPI.getAnalyses();
      setAnalyses(data);
    } catch (error) {
      console.error('Failed to fetch analyses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAnalysis = async (analysisId) => {
    if (!window.confirm('Are you sure you want to delete this analysis? This action cannot be undone.')) {
      return;
    }

    setDeleteLoading(analysisId);
    try {
      await analysisAPI.deleteAnalysis(analysisId);
      setAnalyses(analyses.filter(a => a.id !== analysisId));
    } catch (error) {
      console.error('Failed to delete analysis:', error);
      alert('Failed to delete analysis. Please try again.');
    } finally {
      setDeleteLoading('');
    }
  };

  const handleAnalysisCreated = (newAnalysis) => {
    setAnalyses([newAnalysis, ...analyses]);
    setCurrentView('dashboard');
    // Refresh to get the latest user data
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  const handleViewResults = (analysisId) => {
    setSelectedAnalysisId(analysisId);
    setCurrentView('results');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'processing':
        return <Clock className="h-4 w-4 text-blue-500 animate-pulse" />;
      case 'partial':
        return <AlertCircle className="h-4 w-4 text-orange-500" />;
      case 'failed':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed': return 'Completed';
      case 'processing': return 'Processing';
      case 'partial': return 'Partial';
      case 'failed': return 'Failed';
      case 'pending': return 'Pending';
      default: return status;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (currentView === 'create') {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <CreateAnalysis
          onAnalysisCreated={handleAnalysisCreated}
          onCancel={() => setCurrentView('dashboard')}
        />
      </div>
    );
  }

  if (currentView === 'results' && selectedAnalysisId) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <AnalysisResults
          analysisId={selectedAnalysisId}
          onBack={() => setCurrentView('dashboard')}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Resume AI Dashboard</h1>
              <p className="text-gray-600">AI-powered resume analysis and optimization</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <User className="h-4 w-4" />
                <span>{user?.email}</span>
                {user?.role === 'admin' && (
                  <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs font-medium">
                    Admin
                  </span>
                )}
              </div>
              {user?.role === 'admin' && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => window.location.href = '/admin'}
                >
                  <Shield className="h-4 w-4 mr-2" />
                  Admin Panel
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={logout}>
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Today's Analyses</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {user?.analyses_today || 0}/{user?.max_analyses_per_day || 5}
                  </p>
                </div>
                <Calendar className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Analyses</p>
                  <p className="text-2xl font-bold text-gray-900">{user?.total_analyses || 0}</p>
                </div>
                <BarChart3 className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Completed</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {analyses.filter(a => a.status === 'completed').length}
                  </p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Processing</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {analyses.filter(a => a.status === 'processing').length}
                  </p>
                </div>
                <Clock className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Create New Analysis */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <PlusCircle className="h-5 w-5 text-blue-600" />
                  <span>New Analysis</span>
                </CardTitle>
                <CardDescription>
                  Create a new resume analysis to get AI-powered insights and recommendations.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button 
                  onClick={() => setCurrentView('create')}
                  className="w-full"
                  disabled={!user?.can_create_analysis}
                >
                  <PlusCircle className="h-4 w-4 mr-2" />
                  Create Analysis
                </Button>
                {!user?.can_create_analysis && (
                  <p className="text-xs text-orange-600 mt-2 text-center">
                    Daily limit reached ({user?.analyses_today || 0}/{user?.max_analyses_per_day || 5})
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <TrendingUp className="h-5 w-5 text-green-600" />
                  <span>Quick Stats</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Average Match Score</span>
                    <span className="text-sm font-medium">
                      {analyses.length > 0 
                        ? Math.round(
                            analyses
                              .filter(a => a.match_score)
                              .reduce((sum, a) => sum + a.match_score, 0) / 
                            analyses.filter(a => a.match_score).length
                          ) + '%'
                        : 'N/A'
                      }
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">This Week</span>
                    <span className="text-sm font-medium">
                      {analyses.filter(a => {
                        const weekAgo = new Date();
                        weekAgo.setDate(weekAgo.getDate() - 7);
                        return new Date(a.created_at) > weekAgo;
                      }).length}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Success Rate</span>
                    <span className="text-sm font-medium">
                      {analyses.length > 0
                        ? Math.round((analyses.filter(a => a.status === 'completed').length / analyses.length) * 100) + '%'
                        : 'N/A'
                      }
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Analysis History */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <FileText className="h-5 w-5 text-gray-600" />
                  <span>Analysis History</span>
                </CardTitle>
                <CardDescription>
                  Your recent resume analyses and their results.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="animate-pulse">
                        <div className="h-16 bg-gray-200 rounded"></div>
                      </div>
                    ))}
                  </div>
                ) : analyses.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <p className="text-gray-600 mb-4">No analyses yet</p>
                    <Button onClick={() => setCurrentView('create')}>
                      Create Your First Analysis
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {analyses.map((analysis) => (
                      <div
                        key={analysis.id}
                        className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            {getStatusIcon(analysis.status)}
                            <div>
                              <p className="font-medium text-gray-900">
                                Analysis #{analysis.id.slice(-8)}
                              </p>
                              <p className="text-sm text-gray-600">
                                {formatDate(analysis.created_at)} • {getStatusText(analysis.status)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-3">
                            {analysis.match_score && (
                              <span className="text-sm font-medium text-blue-600">
                                {analysis.match_score}% match
                              </span>
                            )}
                            <div className="flex items-center space-x-1">
                              {analysis.has_results && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleViewResults(analysis.id)}
                                >
                                  <Eye className="h-4 w-4 mr-1" />
                                  View
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeleteAnalysis(analysis.id)}
                                disabled={deleteLoading === analysis.id}
                                className="text-red-600 hover:text-red-700"
                              >
                                {deleteLoading === analysis.id ? (
                                  <Clock className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Trash2 className="h-4 w-4" />
                                )}
                              </Button>
                            </div>
                          </div>
                        </div>
                        
                        {analysis.processing_errors && analysis.processing_errors.length > 0 && (
                          <div className="mt-2 text-xs text-orange-600 bg-orange-50 p-2 rounded">
                            Issues: {analysis.processing_errors.join(', ')}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;